// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import React from 'react';
import { Box, Button, Text } from '~/design-system';
import FieldsTableErrors from '../../../../components/Validation/FieldTableErrors';
import FieldsTableValidation from '../../../../components/Validation/FieldsTableValidation';
import ValidationSkeleton from '../../../../components/Validation/ValidationSkeleton';

/**
 * The props that this component needs.
 * @typedef {object} ValidateImportStepProps
 * @property {string} props.redirectToIfValid - the link to redirect if there is no errors
 * @property {Object[]} props.masterListColumns - the master list columns to this import
 */

/**
 * Renders the page where import is validated
 * @param {ReturnType<useIdentifierValidation> & ValidateImportStepProps} props
 */
export default function IdentifierValidation({
  importId,
  importData,
  isValid,
  masterListColumns,
  loading,
  redirectToIfValid,
  ...rest
}) {
  const [columnErrorSelected, setColumnErrorSelected] = React.useState(null);
  const [removeErrorMessage, setRemoveErrorMessage] = React.useState(null);
  const [removeRows, { loading: removing }] = useMutation(
    REMOVE_ROWS_WITH_IDENTIFIER_ERRORS,
    {
      refetchQueries: ['Validation'],
      awaitRefetchQueries: true,
      onError: (error) => {
        console.error('removeProductDataImportRowsWithIdentifierErrors failed', error);
        setRemoveErrorMessage(
          error?.message ||
            'Could not remove rows with errors. Please try again.'
        );
      },
      onCompleted: (data) => {
        const r = data?.removeProductDataImportRowsWithIdentifierErrors;
        if (r && r.success === false) {
          setRemoveErrorMessage(r.message || 'Could not remove rows with errors.');
        } else {
          setRemoveErrorMessage(null);
        }
      },
    }
  );
  if (loading) return <ValidationSkeleton />;
  // if (isValid && redirectToIfValid) return <Redirect to={redirectToIfValid} />;
  const { sourceFields, identifierFields = [], dataRowIndex } = importData;
  const errorCodes = identifierFields.reduce((acc, { errors }) => {
    errors.forEach(({ code }) => {
      acc[code] = true;
    });
    return acc;
  }, {});
  const canAllowCreation =
    Object.keys(errorCodes).length === 1 && errorCodes.required;
  const errorRowCount = countRowsWithIdentifierErrors(identifierFields);

  return (
    <Box
      sx={{
        textAlign: 'center',
        marginTop: 2,
        padding: '0px 15% 30px 15%',
      }}
      {...rest}
    >
      <FieldsTableValidation
        onMissingRequired={
          <Text variant="smallest">
            Products without specified ID will be created and added to
            Portfolio.
          </Text>
        }
        sourceFields={sourceFields}
        bottlebooksFields={identifierFields}
        onShowColErrors={(col) => setColumnErrorSelected(col)}
        masterListColumns={masterListColumns}
        isValid={isValid}
      />
      {!isValid && errorRowCount > 0 && (
        <Box sx={{ marginTop: 4, marginBottom: 2 }}>
          <Button
            variant="primary"
            disabled={removing}
            onClick={() => {
              setRemoveErrorMessage(null);
              // Apollo's onError already handles UI state. This .catch keeps
              // the rejected promise from triggering an unhandled-rejection
              // warning and logs with importId context (which onError doesn't
              // have in scope) for easier debugging.
              removeRows({ variables: { input: { importId } } }).catch((err) => {
                console.error('removeRows failed for importId', importId, err);
              });
            }}
          >
            {removing ? (
              <Trans>Removing rows…</Trans>
            ) : (
              <Trans>Remove {errorRowCount} rows with errors</Trans>
            )}
          </Button>
          {removeErrorMessage && (
            <Text sx={{ color: 'red', fontSize: 'small', marginTop: 2 }}>
              {removeErrorMessage}
            </Text>
          )}
        </Box>
      )}
      {!isValid && columnErrorSelected && (
        <FieldsTableErrors
          canAllowCreation={canAllowCreation}
          key={columnErrorSelected.key}
          sx={{ marginTop: 5, marginBottom: 6 }}
          sourceFields={sourceFields}
          bottlebooksFields={identifierFields}
          column={columnErrorSelected}
          dataRowIndex={dataRowIndex}
          masterListColumns={masterListColumns}
        />
      )}
    </Box>
  );
}

function countRowsWithIdentifierErrors(identifierFields = []) {
  // Each error has a row `index`. The same row can hit multiple identifier
  // fields so we dedupe before showing the count.
  const indexes = new Set();
  identifierFields.forEach(({ errors = [] }) => {
    errors.forEach(({ index }) => {
      if (typeof index === 'number') indexes.add(index);
    });
  });
  return indexes.size;
}

const REMOVE_ROWS_WITH_IDENTIFIER_ERRORS = gql`
  mutation RemoveProductDataImportRowsWithIdentifierErrors(
    $input: RemoveProductDataImportRowsWithIdentifierErrorsInput!
  ) {
    removeProductDataImportRowsWithIdentifierErrors(input: $input) {
      success
      code
      message
    }
  }
`;

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {Object} props
 * @param {String} props.workspaceId
 * @param {String} props.importId
 * @returns
 */
export function useIdentifierValidation({ workspaceId, importId }) {
  const { data, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });

  return {
    loading,
    ...afterQuery(data),
  };
}

function afterQuery(data) {
  const importData = data?.workspace?.dataImport;
  const isValid = !Boolean(
    importData?.identifierFields?.find(({ errors }) => errors?.length > 0)
  );
  return {
    importData,
    isValid,
  };
}

const QUERY = gql`
  query Validation($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        dataRowIndex
        identifierFields {
          bottlebooksFieldName
          importFieldName
          errors {
            index
            code
            value
          }
        }
      }
    }
  }
`;
